// Detects critical findings in DPCA (peritoneal dialysis) exchanges.
// Feeds the same WhatsApp critical alert used across the app.

export const APPEARANCE_LABELS = {
  claro: "Claro",
  turbio: "Turbio",
  hematico: "Hematico (con sangre)",
  fibrina: "Con fibrina",
};

export const CONCENTRATION_LABELS = {
  "1.5": "1.5% (amarilla)",
  "2.5": "2.5% (verde)",
  "4.25": "4.25% (roja)",
  icodextrina: "Icodextrina",
  otra: "Otra",
};

export const CONCENTRATION_COLORS = {
  "1.5": { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B" },
  "2.5": { bg: "#DCFCE7", text: "#166534", dot: "#22C55E" },
  "4.25": { bg: "#FEE2E2", text: "#991B1B", dot: "#EF4444" },
  icodextrina: { bg: "#EDE9FE", text: "#5B21B6", dot: "#8B5CF6" },
  otra: { bg: "#F1F5F9", text: "#334155", dot: "#64748B" },
};

// Compute UF for a single exchange (drained - infused).
export function exchangeUF(ex) {
  const inf = Number(ex?.infused_ml) || 0;
  const drn = Number(ex?.drained_ml) || 0;
  if (!inf && !drn) return null;
  return drn - inf;
}

// Return a list of human-readable critical reasons for the DPCA data of a program.
// Used by supervisor-alert (WhatsApp) and by red banners in the UI.
export function detectDialysisCriticalReasons(pd) {
  const reasons = [];
  const exchanges = Array.isArray(pd?.exchanges) ? pd.exchanges : [];
  exchanges.forEach((ex) => {
    const label = ex.start_time ? `${ex.start_time}` : `#${ex.number || "?"}`;
    // Non-clear liquid = infection risk
    if (ex.appearance && ex.appearance !== "claro") {
      reasons.push(
        `DPCA ${label}: liquido ${APPEARANCE_LABELS[ex.appearance] || ex.appearance}`
      );
    }
    // Very negative UF (< -500 mL) = retention / drenaje insuficiente
    const uf = exchangeUF(ex);
    if (uf !== null && uf < -500) {
      reasons.push(`DPCA ${label}: UF muy negativa (${uf} ml)`);
    }
    // Drenaje incompleto (< 1500 ml with a normal infusion volume)
    const drn = Number(ex.drained_ml) || 0;
    const inf = Number(ex.infused_ml) || 0;
    if (inf >= 1500 && drn > 0 && drn < 1500) {
      reasons.push(`DPCA ${label}: drenaje incompleto (${drn} ml)`);
    }
  });
  return reasons;
}

// Aggregate totals for the shift (used in section footer and PDF).
export function summarizeDialysisShift(pd) {
  const exchanges = Array.isArray(pd?.exchanges) ? pd.exchanges : [];
  const infused = exchanges.reduce((a, e) => a + (Number(e.infused_ml) || 0), 0);
  const drained = exchanges.reduce((a, e) => a + (Number(e.drained_ml) || 0), 0);
  const uf = exchanges.length ? drained - infused : 0;
  const nonClear = exchanges.filter((e) => e.appearance && e.appearance !== "claro").length;
  return {
    count: exchanges.length,
    infused_ml: infused,
    drained_ml: drained,
    ultrafiltration_ml: uf,
    non_clear_count: nonClear,
  };
}
